import 'components/DSYS/editor/dynamicNode/registerDynamicNodes';

import {Chip} from '@dropbox/dig-components/chip';
import {Menu} from '@dropbox/dig-components/menu';
import {UIIcon} from '@dropbox/dig-icons';
import {DeleteLine, PersonLine} from '@dropbox/dig-icons/assets';
import {useOptionalEditorFocusManager} from 'components/DSYS/CollabProvider';
import {getDynamicNodeDef} from 'components/DSYS/editor/dynamicNode/registry';
import {t} from 'i18next';
import {
  $getNodeByKey,
  DecoratorNode,
  DOMConversionMap,
  DOMExportOutput,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import {useIsTemplateMode} from 'plugins/enhancedHeadings';
import {
  type MouseEvent as ReactMouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

type SerializedDynamicNode = Spread<
  {
    type: 'dynamic_node';
    kindId: string;
    binding?: unknown;
    value?: unknown;
  },
  SerializedLexicalNode
>;

function bindingFromImportedJson(kindId: string, rawBinding: unknown): unknown {
  const def = getDynamicNodeDef(kindId);
  if (def?.normalizeImportedBinding) {
    return def.normalizeImportedBinding(rawBinding);
  }
  if (
    rawBinding &&
    typeof rawBinding === 'object' &&
    !Array.isArray(rawBinding) &&
    (rawBinding as {kind?: unknown}).kind === kindId
  ) {
    return rawBinding;
  }
  return def?.defaultBinding ?? {};
}

function dynamicNodePlainExport(kindId: string, value: unknown): string {
  const def = getDynamicNodeDef(kindId);
  return def ? def.exportHtml({value}) : '';
}

export class DynamicNode extends DecoratorNode<ReactNode> {
  __kindId: string;
  __binding: unknown;
  __value: unknown;

  static getType(): string {
    return 'dynamic_node';
  }

  static clone(node: DynamicNode): DynamicNode {
    const cloned = new DynamicNode(node.__kindId, node.__binding, node.__value, node.__key);
    return cloned;
  }

  constructor(kindId: string, binding: unknown, value?: unknown, key?: NodeKey) {
    super(key);
    this.__kindId = kindId;
    this.__binding = binding;
    this.__value = value;
  }

  static importJSON(json: SerializedDynamicNode): DynamicNode {
    const kindId = json.kindId;
    if (typeof kindId !== 'string' || !kindId) {
      throw new Error('dynamic_node: kindId is required');
    }
    const binding = bindingFromImportedJson(kindId, json.binding);
    return new DynamicNode(kindId, binding, json.value);
  }

  exportJSON(): SerializedDynamicNode {
    return {
      type: 'dynamic_node',
      version: 1,
      kindId: this.__kindId,
      binding: this.__binding,
      value: this.__value,
    };
  }

  createDOM(): HTMLElement {
    return document.createElement('span');
  }

  updateDOM(): boolean {
    return false;
  }

  isInline(): boolean {
    return true;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }

  getKindId(): string {
    return this.__kindId;
  }

  getBinding(): unknown {
    return this.__binding;
  }

  getValue(): unknown {
    return this.__value;
  }

  setValue(value: unknown): void {
    const self = this.getWritable();
    self.__value = value;
  }

  getTextContent(): string {
    return dynamicNodePlainExport(this.__kindId, this.__value);
  }

  exportDOM(): DOMExportOutput {
    const el = document.createElement('span');
    el.textContent = dynamicNodePlainExport(this.__kindId, this.__value);
    el.setAttribute('data-lexical-type', 'dynamic_node');
    el.setAttribute('data-dynamic-kind-id', this.__kindId);
    return {element: el};
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (domNode.getAttribute('data-lexical-type') !== 'dynamic_node') {
          return null;
        }
        return {
          conversion: (element: HTMLElement) => {
            const kindId = element.getAttribute('data-dynamic-kind-id');
            if (!kindId) return null;
            const binding = getDynamicNodeDef(kindId)?.defaultBinding ?? {};
            return {node: new DynamicNode(kindId, binding)};
          },
          priority: 1,
        };
      },
    };
  }

  decorate(editor: LexicalEditor): ReactNode {
    return (
      <DynamicNodeDecorator
        nodeKey={this.getKey()}
        editor={editor}
        kindId={this.__kindId}
        binding={this.__binding}
        initialValue={this.__value}
      />
    );
  }
}

export function $createDynamicNode(kindId: string, binding: unknown, value?: unknown): DynamicNode {
  return new DynamicNode(kindId, binding, value);
}

export function $isDynamicNode(node: LexicalNode | null | undefined): node is DynamicNode {
  return node instanceof DynamicNode;
}

const DynamicNodeTemplateChip = ({
  nodeKey,
  editor,
  kindId,
}: {
  nodeKey: NodeKey;
  editor: LexicalEditor;
  kindId: string;
}) => {
  const def = getDynamicNodeDef(kindId);
  const labelKey = def?.labelKey ?? 'typeahead_owner';

  const remove = useCallback(() => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isDynamicNode(node)) {
        node.remove();
      }
    });
  }, [editor, nodeKey]);

  return (
    <Menu.Wrapper
      onSelection={(selectionId) => {
        if (selectionId === 'remove') {
          remove();
        }
      }}
      shouldPropagateClickOutsideMouseEvents
    >
      {({getContentProps, getTriggerProps}) => (
        <>
          <Chip {...getTriggerProps()} size="small" withDropdownIcon>
            <Chip.IconAccessory>
              <UIIcon src={PersonLine} />
            </Chip.IconAccessory>
            <Chip.Content>{t(labelKey)}</Chip.Content>
          </Chip>
          <Menu.Content {...getContentProps()} minWidth="200px">
            <Menu.Segment>
              <Menu.ActionItem value="remove" withLeftAccessory={<UIIcon src={DeleteLine} />}>
                {t('typeahead_remove')}
              </Menu.ActionItem>
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

const DynamicNodeDecorator = ({
  nodeKey,
  editor,
  kindId,
  binding,
  initialValue,
}: {
  nodeKey: NodeKey;
  editor: LexicalEditor;
  kindId: string;
  binding: unknown;
  initialValue: unknown;
}) => {
  const isTemplateMode = useIsTemplateMode();
  const focusManager = useOptionalEditorFocusManager();
  const [lastInitialValue, setLastInitialValue] = useState(initialValue);
  const [value, setValueState] = useState(initialValue);

  useEffect(() => {
    setLastInitialValue(initialValue);
    setValueState(initialValue);
  }, [initialValue]);

  const setValue = useCallback(
    (next: unknown) => {
      setValueState(next);
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isDynamicNode(node)) {
          node.setValue(next);
        }
      });
    },
    [editor, nodeKey]
  );

  const handleMouseDownCapture = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (event.defaultPrevented || event.button !== 0) {
        return;
      }

      focusManager?.clearAll();
    },
    [focusManager]
  );

  if (isTemplateMode) {
    return <DynamicNodeTemplateChip nodeKey={nodeKey} editor={editor} kindId={kindId} />;
  }

  const def = getDynamicNodeDef(kindId);
  if (!def) {
    return null;
  }

  const Component = def.Component;
  // When setValue is called, DynamicNodeDecorator may render with a new initialValue, before `value` from the `useState` hook is actually updated. In that case, we want to use the new initialValue to render, instead of the stale `value` from the `useState` hook.
  // If we don't do this, Component may recognize the mismatch of value from other state, and try to call `setValue` again.
  // That will trigger a re-render, still with the old value, resulting in an infinite loop.
  const valueToRender = initialValue !== lastInitialValue ? initialValue : value;
  return (
    // Mark decorator UI as an editor boundary so the root editor can clear stale selection/toolbars
    // when users click embedded tables, controls, or nested read-only editors.
    <span
      data-dynamic-node-focus-boundary="true"
      data-dynamic-node-kind-id={kindId}
      onMouseDownCapture={handleMouseDownCapture}
      style={{display: 'contents'}}
    >
      <Component
        nodeKey={nodeKey}
        editor={editor}
        kindId={kindId}
        binding={binding}
        value={valueToRender}
        setValue={setValue}
      />
    </span>
  );
};
